import JSZip from "jszip";
import { exportSnapshot, listAudio } from "./storage.js";

/**
 * 根据 MIME 类型选择用户备份中的音频扩展名。
 *
 * @param {string} mimeType 浏览器 Blob MIME 类型。
 * @returns {string} 不含点号的扩展名。
 */
function audioExtension(mimeType) {
  const normalized = String(mimeType || "").split(";", 1)[0].toLowerCase();
  return {
    "audio/wav": "wav",
    "audio/x-wav": "wav",
    "audio/mpeg": "mp3",
    "audio/mp3": "mp3",
    "audio/ogg": "ogg",
    "audio/webm": "webm",
  }[normalized] || "bin";
}

/**
 * 触发一个只存在于浏览器内存中的文件下载。
 *
 * @param {Blob} blob 文件内容。
 * @param {string} filename 下载文件名。
 * @returns {void}
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/**
 * 生成题库、提示词、历史和全部录音的 ZIP 备份。
 *
 * @returns {Promise<Blob>} 完整备份 ZIP。
 */
export async function buildLocalBackup() {
  const [snapshot, audioRecords] = await Promise.all([exportSnapshot(), listAudio()]);
  const archive = new JSZip();
  archive.file("data.json", `${JSON.stringify(snapshot, null, 2)}\n`);
  for (const record of audioRecords) {
    const safeId = String(record.id || crypto.randomUUID()).replace(/[^A-Za-z0-9_-]/g, "_");
    archive.file(`audio/${safeId}.${audioExtension(record.mimeType || record.blob?.type)}`, await record.blob.arrayBuffer());
  }
  return archive.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
}

/**
 * 导出题库、提示词、历史和全部录音为 ZIP 备份。
 *
 * @returns {Promise<void>} ZIP 文件生成并交给浏览器后的结果。
 */
export async function exportLocalBackup() {
  const blob = await buildLocalBackup();
  const date = new Date().toISOString().slice(0, 10);
  downloadBlob(blob, `speaking-studio-backup-${date}.zip`);
}

/**
 * 只导出当前完整题库，便于在其他设备重新导入。
 *
 * @param {Array<object>} cards 当前题卡数组。
 * @returns {void}
 */
export function exportQuestionBank(cards) {
  const blob = new Blob([`${JSON.stringify(cards, null, 2)}\n`], { type: "application/json" });
  downloadBlob(blob, "speaking-studio-question-bank.json");
}
